import { AsyncSelectInput } from '@/components/Forms/AsyncSelect';
import FormSection from '@/components/Forms/FormSection';
import { debounceAsync } from '@/helpers/debounceAsync';
import { intercomTarget } from '@/helpers/intercomTarget';
import usePermissions from '@/hooks/usePermissions';
import { useSearchProductsMutation } from '@/services/productCatalog';
import {
  Box,
  Button,
  FormikRadioGroup,
  FormikSelectInput,
  FormikTextInput,
  Modal,
  SimulatedEventPayloadType,
  toast,
  useOpenClose,
} from '@palmetto/palmetto-components';
import { Field, FieldArray, useFormikContext } from 'formik';
import { SyntheticEvent, useEffect } from 'react';
import { Link } from 'react-router-dom';

import {
  UserPermissions,
  WaterHeaterEquipmentItem,
  WaterHeaterEquipmentType,
  WaterHeaterEquipmentTypeLabels,
  WaterHeaterFuelType,
  WaterHeaterFuelTypeLabels,
  WaterHeaterPreliminaryEquipmentItem,
  WaterHeaterTankCapacityRanges,
  WaterHeaterTanklessFlowRateRanges,
  WaterHeaterType,
  WaterHeaterTypeLabels,
} from 'types';

interface EquipmentAndMaterialsSectionProps {
  systemIndex: number;
  isPreliminary?: boolean;
  helpLink?: string;
  isPackageEditable?: boolean;
}

const dataKey = 'equipment' as const;
const label = 'Equipment & Materials' as const;

const fuelTypeOptions = Object.values(WaterHeaterFuelType).map((type) => ({
  id: type,
  label: WaterHeaterFuelTypeLabels[type],
  value: type,
}));

const waterHeaterTypeOptions = Object.values(WaterHeaterType).map((type) => ({
  id: type,
  label: WaterHeaterTypeLabels[type],
  value: type,
}));

const tankCapacityOptions = WaterHeaterTankCapacityRanges.map((range) => ({
  label: range.label,
  value: range.value,
}));

const tanklessFlowRateOptions = WaterHeaterTanklessFlowRateRanges.map((range) => ({
  label: range.label,
  value: range.value,
}));

export const EquipmentAndMaterialsSection = ({
  systemIndex,
  isPreliminary = false,
  helpLink,
  isPackageEditable = true,
}: EquipmentAndMaterialsSectionProps) => {
  const permissions = usePermissions();
  const userOnlyHasReaderPermission = permissions?.every((permission) =>
    permission.includes(UserPermissions.reader),
  );
  const { values, setFieldValue, handleBlur } = useFormikContext<any>();

  const [triggerSearch] = useSearchProductsMutation();
  const handleSearchText = async (searchText: string, productType: string) => {
    const { data, error } = await triggerSearch({
      ...(searchText ? { query: searchText } : {}),
      limit: 20,
      page: 1,
      filters: [
        {
          filter: 'attribute',
          key: 'lightreach-product-type',
          operation: {
            equals: productType,
          },
        },
        {
          filter: 'availableOnDate',
          operation: {
            equals: new Date().toISOString(),
          },
        },
      ],
    });
    if (error) {
      toast.error('Error searching products. Please try again.');
      return undefined;
    }
    const productData = data?.data?.map((product) => {
      const model = product.attributes.find((attr) => attr.key === 'model-number')?.value ?? '';
      const productType = product.attributes.find((attr) => attr.key === 'lightreach-product-type')?.value ?? '';
      const isSerialNumberRequired =
        product.attributes.find((attr) => attr.key === 'lightreach-serial-number-required')?.value ?? false;
      return {
        label: `${product.manufacturer} - ${model}`,
        value: product.id,
        manufacturer: product.manufacturer,
        model,
        productType,
        name: product.name,
        isSerialNumberRequired,
      };
    });
    return productData?.length && (data?.paging?.totalPages || 0) > 1
      ? [
          {
            label: `${data?.paging.totalRecords} results found. Continue typing to narrow down results.`,
            value: undefined,
          },
          ...productData,
        ]
      : productData;
  };

  const debouncedSearch = debounceAsync(
    (value: string, equipmentType: WaterHeaterEquipmentType) => handleSearchText(value, equipmentType),
    400,
    2,
    true,
  );

  // For preliminary design, auto-add a water heater equipment item if none exists
  const equipmentItemsLength = values?.systems?.[systemIndex]?.[dataKey]?.items?.length ?? 0;
  useEffect(() => {
    if (isPreliminary && equipmentItemsLength === 0) {
      setFieldValue(`systems.${systemIndex}[${dataKey}].items`, [
        {
          type: WaterHeaterEquipmentType.waterHeater,
          quantity: 1,
        },
      ]);
    }
  }, [isPreliminary, systemIndex, equipmentItemsLength, setFieldValue]);

  const {
    isOpen: isQuantityModalOpen,
    handleOpen: openQuantityModal,
    handleClose: closeQuantityModal,
  } = useOpenClose();

  const handleQuantityChange = (_index: number, newValue: number) => {
    if (newValue > 1) {
      openQuantityModal();
    }
  };

  if (isPreliminary) {
    return (
      <>
        <Modal
          isOpen={isQuantityModalOpen}
          onDismiss={closeQuantityModal}
          title="Additional Water Heater Required"
          size="sm"
        >
          <Box padding="lg" gap="lg">
            <Box>
              Each water heater system must be entered separately. To add another water heater, please use the
              &quot;Add System&quot; button below to create an additional system.
            </Box>
            <Box direction="row" justifyContent="flex-end">
              <Button variant="primary" onClick={closeQuantityModal}>
                Got it
              </Button>
            </Box>
          </Box>
        </Modal>
        <FormSection
          title={label}
          description={
            <Box gap="sm" fontSize="xs">
              <Box>Configure the water heater specifications for this system.</Box>
              {helpLink && (
                <Box display="block">
                  For more information, check the{' '}
                  <Link to={helpLink} target="_blank" rel="noopener noreferrer">
                    Knowledge Base
                  </Link>
                </Box>
              )}
            </Box>
          }
          baseStyle={{
            borderWidth: '0',
            'data-intercom-target': intercomTarget('system-design', label),
          }}
        >
          <Box gap="xl">
            {values?.systems[systemIndex]?.[dataKey]?.items?.map?.(
              (equipmentItem: WaterHeaterPreliminaryEquipmentItem, index: number) => {
                const showTankCapacity =
                  equipmentItem.waterHeaterType === WaterHeaterType.tank ||
                  equipmentItem.waterHeaterType === WaterHeaterType.heatPump;
                const showTanklessFlowRate = equipmentItem.waterHeaterType === WaterHeaterType.tankless;

                // Get the selected option for tank capacity dropdown
                const selectedTankCapacity = equipmentItem.tankCapacity?.value
                  ? tankCapacityOptions.find((opt) => opt.value === equipmentItem.tankCapacity?.value)
                  : null;

                // Get the selected option for tankless flow rate dropdown
                const selectedFlowRate = equipmentItem.tanklessFlowRate?.value
                  ? tanklessFlowRateOptions.find((opt) => opt.value === equipmentItem.tanklessFlowRate?.value)
                  : null;

                return (
                  <Box
                    key={`system-${systemIndex}-${dataKey}-${equipmentItem.type}-${index}`}
                    direction={{ base: 'column', tablet: 'row' }}
                    gap="lg"
                    padding={{ base: 'md', tablet: 'lg' }}
                    background="secondary"
                    radius="md"
                  >
                    <Box flex="auto" gap="lg" minWidth="0">
                      <Box direction={{ base: 'column', desktop: 'row' }} gap={{ base: 'lg', desktop: 'xl' }}>
                        <Box flex="auto" minWidth="0">
                          <Box fontSize="sm" fontWeight="medium" margin="0 0 sm 0">
                            Fuel Type
                          </Box>
                          <Field
                            component={FormikRadioGroup}
                            id={`systems.${systemIndex}[${dataKey}].items.${index}.fuelType`}
                            name={`systems.${systemIndex}[${dataKey}].items.${index}.fuelType`}
                            options={fuelTypeOptions}
                            isDisabled={userOnlyHasReaderPermission || !isPackageEditable}
                            onChange={(event: SyntheticEvent<HTMLInputElement>) => {
                              const selectedValue = (event.target as HTMLInputElement).value;
                              if (typeof selectedValue !== 'undefined') {
                                setFieldValue(
                                  `systems.${systemIndex}[${dataKey}].items.${index}.fuelType`,
                                  selectedValue,
                                );
                              }
                            }}
                          />
                        </Box>

                        <Box flex="auto" minWidth="0">
                          <Box fontSize="sm" fontWeight="medium" margin="0 0 sm 0">
                            Water Heater Type
                          </Box>
                          <Field
                            component={FormikRadioGroup}
                            id={`systems.${systemIndex}[${dataKey}].items.${index}.waterHeaterType`}
                            name={`systems.${systemIndex}[${dataKey}].items.${index}.waterHeaterType`}
                            options={waterHeaterTypeOptions}
                            isDisabled={userOnlyHasReaderPermission || !isPackageEditable}
                            onChange={(event: SyntheticEvent<HTMLInputElement>) => {
                              const selectedValue = (event.target as HTMLInputElement).value;
                              if (typeof selectedValue !== 'undefined') {
                                setFieldValue(
                                  `systems.${systemIndex}[${dataKey}].items.${index}.waterHeaterType`,
                                  selectedValue,
                                );
                                // Clear capacity/flow rate when type changes
                                setFieldValue(
                                  `systems.${systemIndex}[${dataKey}].items.${index}.tankCapacity`,
                                  undefined,
                                );
                                setFieldValue(
                                  `systems.${systemIndex}[${dataKey}].items.${index}.tanklessFlowRate`,
                                  undefined,
                                );
                              }
                            }}
                          />
                        </Box>
                      </Box>
                      {showTankCapacity && (
                        <Box maxWidth="3xl">
                          <Field
                            label="Tank Capacity"
                            name={`systems.${systemIndex}[${dataKey}].items.${index}.tankCapacitySelect`}
                            id={`systems.${systemIndex}[${dataKey}].items.${index}.tankCapacitySelect`}
                            placeholder="Select tank capacity..."
                            component={FormikSelectInput}
                            options={tankCapacityOptions}
                            isDisabled={userOnlyHasReaderPermission || !isPackageEditable}
                            onChange={(event: { target: { value: { value: string; label: string } | null } }) => {
                              const selected = event?.target?.value;
                              setFieldValue(
                                `systems.${systemIndex}[${dataKey}].items.${index}.tankCapacitySelect`,
                                selected ?? null,
                              );
                              if (selected) {
                                setFieldValue(`systems.${systemIndex}[${dataKey}].items.${index}.tankCapacity`, {
                                  value: selected.value,
                                  unit: 'gallons',
                                });
                              } else {
                                setFieldValue(
                                  `systems.${systemIndex}[${dataKey}].items.${index}.tankCapacity`,
                                  undefined,
                                );
                              }
                            }}
                            value={selectedTankCapacity}
                          />
                        </Box>
                      )}
                      {showTanklessFlowRate && (
                        <Box maxWidth="3xl">
                          <Field
                            label="Tankless Flow Rate"
                            name={`systems.${systemIndex}[${dataKey}].items.${index}.tanklessFlowRateSelect`}
                            id={`systems.${systemIndex}[${dataKey}].items.${index}.tanklessFlowRateSelect`}
                            placeholder="Select flow rate..."
                            component={FormikSelectInput}
                            options={tanklessFlowRateOptions}
                            isDisabled={userOnlyHasReaderPermission || !isPackageEditable}
                            onChange={(event: { target: { value: { value: string; label: string } | null } }) => {
                              const selected = event?.target?.value;
                              setFieldValue(
                                `systems.${systemIndex}[${dataKey}].items.${index}.tanklessFlowRateSelect`,
                                selected ?? null,
                              );
                              if (selected) {
                                setFieldValue(`systems.${systemIndex}[${dataKey}].items.${index}.tanklessFlowRate`, {
                                  value: selected.value,
                                  unit: 'GPM',
                                });
                              } else {
                                setFieldValue(
                                  `systems.${systemIndex}[${dataKey}].items.${index}.tanklessFlowRate`,
                                  undefined,
                                );
                              }
                            }}
                            value={selectedFlowRate}
                          />
                        </Box>
                      )}
                    </Box>

                    <Box
                      direction={{ base: 'row', tablet: 'column' }}
                      gap="sm"
                      alignItems={{ base: 'flex-end', tablet: 'flex-start' }}
                      justifyContent={{ base: 'space-between', tablet: 'flex-start' }}
                      flex={{ tablet: 'none' }}
                    >
                      <Box width={{ base: '2xl', tablet: 'lg' }}>
                        <Field
                          label="Quantity"
                          type="number"
                          name={`systems.${systemIndex}[${dataKey}].items.${index}.quantity`}
                          id={`systems.${systemIndex}[${dataKey}].items.${index}.quantity`}
                          component={FormikTextInput}
                          min={1}
                          max={10}
                          isDisabled={userOnlyHasReaderPermission || !isPackageEditable}
                          onChange={(event: SimulatedEventPayloadType) => {
                            const value = parseInt(event?.target?.value, 10);
                            if (!isNaN(value) && value > 1) {
                              handleQuantityChange(index, value);
                            }
                          }}
                          onBlur={(event: SimulatedEventPayloadType) => {
                            if (event?.target?.value?.trim() === '') {
                              setFieldValue(`systems.${systemIndex}[${dataKey}].items.${index}.quantity`, 1);
                            }
                          }}
                        />
                      </Box>
                    </Box>
                  </Box>
                );
              },
            )}
          </Box>
        </FormSection>
      </>
    );
  }

  // Non-preliminary (final) design UI
  return (
    <>
      <Modal
        isOpen={isQuantityModalOpen}
        onDismiss={closeQuantityModal}
        title="Additional Water Heater Required"
        size="sm"
      >
        <Box padding="lg" gap="lg">
          <Box>
            Each water heater system must be entered separately. To add another water heater, please use the
            &quot;Add System&quot; button below to create an additional system.
          </Box>
          <Box direction="row" justifyContent="flex-end">
            <Button variant="primary" onClick={closeQuantityModal}>
              Got it
            </Button>
          </Box>
        </Box>
      </Modal>
      <FormSection
        title={label}
        description={
          <Box gap="sm" fontSize="xs">
            <Box>Update applicable {label} for the proposal.</Box>
            {helpLink && (
              <Box display="block">
                For more information about System Categories, check the{' '}
                <Link to={helpLink} target="_blank" rel="noopener noreferrer">
                  Knowledge Base
                </Link>
              </Box>
            )}
          </Box>
        }
        baseStyle={{
          borderWidth: '0',
          'data-intercom-target': intercomTarget('system-design', label),
        }}
      >
        <FieldArray
          name={`systems.${systemIndex}[${dataKey}].items`}
          render={() => {
            return (
              <Box gap="lg">
                {values?.systems[systemIndex]?.[dataKey]?.items?.map?.(
                  (
                    equipmentMaterial: WaterHeaterEquipmentItem & {
                      isSerialNumberRequired?: boolean;
                      isOther?: boolean;
                    },
                    index: number,
                  ) => {
                    const showTankCapacity =
                      equipmentMaterial.waterHeaterType === WaterHeaterType.tank ||
                      equipmentMaterial.waterHeaterType === WaterHeaterType.heatPump;
                    const showTanklessFlowRate = equipmentMaterial.waterHeaterType === WaterHeaterType.tankless;

                    // Get the selected option for tank capacity dropdown
                    const selectedTankCapacity = equipmentMaterial.tankCapacity?.value
                      ? tankCapacityOptions.find((opt) => opt.value === equipmentMaterial.tankCapacity?.value)
                      : null;

                    // Get the selected option for tankless flow rate dropdown
                    const selectedFlowRate = equipmentMaterial.tanklessFlowRate?.value
                      ? tanklessFlowRateOptions.find(
                          (opt) => opt.value === equipmentMaterial.tanklessFlowRate?.value,
                        )
                      : null;

                    return (
                      <Box
                        key={`system-${systemIndex}-${dataKey}-${equipmentMaterial.type}-${index}`}
                        direction={{ base: 'column', tablet: 'row' }}
                        gap="lg"
                        padding={{ base: 'md', tablet: 'lg' }}
                        background="secondary"
                        radius="md"
                      >
                        <Box flex="auto" gap="lg" minWidth="0">
                          <Box direction="row" justifyContent="space-between" alignItems="flex-start" gap="md">
                            <Box direction="column" gap="sm" flex="auto" minWidth="0">
                              <Box fontSize="md" fontWeight="medium">
                                {WaterHeaterEquipmentTypeLabels[equipmentMaterial.type]}
                              </Box>
                              {equipmentMaterial.isOther ? (
                                <Box
                                  direction={{ base: 'column', desktop: 'row' }}
                                  gap={{ base: 'sm', desktop: 'md' }}
                                >
                                  <Box flex="auto">
                                    <Field
                                      label={'Manufacturer'}
                                      name={`systems.${systemIndex}[${dataKey}].items.${index}.manufacturer`}
                                      id={`systems.${systemIndex}[${dataKey}].items.${index}.manufacturer`}
                                      component={FormikTextInput}
                                      isRequired
                                      isDisabled={!isPackageEditable}
                                    />
                                  </Box>
                                  <Box flex="auto">
                                    <Field
                                      label={'Model'}
                                      name={`systems.${systemIndex}[${dataKey}].items.${index}.model`}
                                      id={`systems.${systemIndex}[${dataKey}].items.${index}.model`}
                                      component={FormikTextInput}
                                      isRequired
                                      isDisabled={!isPackageEditable}
                                    />
                                  </Box>
                                </Box>
                              ) : null}
                              {equipmentMaterial.manufacturer &&
                              equipmentMaterial.model &&
                              !equipmentMaterial.isOther ? (
                                <>
                                  <Box fontSize="sm">
                                    {equipmentMaterial.manufacturer} - {equipmentMaterial.model}{' '}
                                  </Box>
                                  <Box fontSize="sm">
                                    {equipmentMaterial.quantity ? `Quantity: ${equipmentMaterial.quantity}` : ''}
                                  </Box>
                                </>
                              ) : null}
                              {!(equipmentMaterial.manufacturer && equipmentMaterial.manufacturer !== '') &&
                              !(equipmentMaterial.model && equipmentMaterial.model !== '') &&
                              !equipmentMaterial.isOther ? (
                                <AsyncSelectInput
                                  id={`systems.${systemIndex}[${dataKey}].items.selectedItem`}
                                  name={`systems.${systemIndex}[${dataKey}].items.selectedItem`}
                                  label={`Search ${label}`}
                                  loadOnOpen={!!values?.systems[systemIndex]?.[dataKey]?.items?.[index]?.type}
                                  loadOptions={async (input: string) => {
                                    const options = await debouncedSearch(
                                      input,
                                      values?.systems[systemIndex]?.[dataKey]?.items?.[index]?.type,
                                    );
                                    return [
                                      ...options,
                                      {
                                        label: (
                                          <Button
                                            as="button"
                                            variant="secondary"
                                            size="sm"
                                            onClick={() => {
                                              setFieldValue(
                                                `systems.${systemIndex}[${dataKey}].items.${index}.manufacturer`,
                                                '',
                                              );
                                              setFieldValue(
                                                `systems.${systemIndex}[${dataKey}].items.${index}.model`,
                                                '',
                                              );
                                              setFieldValue(
                                                `systems.${systemIndex}[${dataKey}].items.${index}.quantity`,
                                                1,
                                              );
                                              setFieldValue(
                                                `systems.${systemIndex}[${dataKey}].items.${index}.productId`,
                                                'other',
                                              );
                                              setFieldValue(
                                                `systems.${systemIndex}[${dataKey}].items.${index}.name`,
                                                '',
                                              );
                                              setFieldValue(
                                                `systems.${systemIndex}[${dataKey}].items.${index}.isOther`,
                                                true,
                                              );
                                            }}
                                          >
                                            Enter item not found in search
                                          </Button>
                                        ),
                                        value: 'other',
                                      },
                                    ];
                                  }}
                                  isDisabled={userOnlyHasReaderPermission || !isPackageEditable}
                                  onChange={(event) => {
                                    if (
                                      event?.target?.value &&
                                      event.target.value.value &&
                                      event.target.value.value !== 'other'
                                    ) {
                                      setFieldValue(
                                        `systems.${systemIndex}[${dataKey}].items.${index}.manufacturer`,
                                        event?.target?.value.manufacturer,
                                      );
                                      setFieldValue(
                                        `systems.${systemIndex}[${dataKey}].items.${index}.model`,
                                        event?.target?.value.model,
                                      );
                                      setFieldValue(`systems.${systemIndex}[${dataKey}].items.${index}.quantity`, 1);
                                      setFieldValue(
                                        `systems.${systemIndex}[${dataKey}].items.${index}.productId`,
                                        event?.target?.value.value,
                                      );
                                      setFieldValue(
                                        `systems.${systemIndex}[${dataKey}].items.${index}.name`,
                                        event?.target?.value.name,
                                      );
                                      setFieldValue(
                                        `systems.${systemIndex}[${dataKey}].items.${index}.isOther`,
                                        false,
                                      );
                                    }
                                  }}
                                  onBlur={handleBlur}
                                  isClearable
                                  value={''}
                                  placeholder="Search by Model or Name..."
                                  noOptionsMessage={({ inputValue }: { inputValue: string | React.JSX.Element }) => {
                                    if (!inputValue) return `Type to search...`;
                                    return (
                                      <Box display="flex" direction="column" gap="sm">
                                        <div>Not found</div>
                                        <Button
                                          as="button"
                                          variant="secondary"
                                          size="sm"
                                          isDisabled={!isPackageEditable}
                                          onClick={() => {
                                            setFieldValue(
                                              `systems.${systemIndex}[${dataKey}].items.${index}.manufacturer`,
                                              '',
                                            );
                                            setFieldValue(
                                              `systems.${systemIndex}[${dataKey}].items.${index}.model`,
                                              '',
                                            );
                                            setFieldValue(
                                              `systems.${systemIndex}[${dataKey}].items.${index}.quantity`,
                                              1,
                                            );
                                            setFieldValue(
                                              `systems.${systemIndex}[${dataKey}].items.${index}.productId`,
                                              'other',
                                            );
                                            setFieldValue(
                                              `systems.${systemIndex}[${dataKey}].items.${index}.name`,
                                              '',
                                            );
                                            setFieldValue(
                                              `systems.${systemIndex}[${dataKey}].items.${index}.isOther`,
                                              true,
                                            );
                                          }}
                                        >
                                          Enter item not found in search
                                        </Button>
                                      </Box>
                                    );
                                  }}
                                />
                              ) : null}
                            </Box>
                          </Box>

                          <Box direction={{ base: 'column', desktop: 'row' }} gap={{ base: 'lg', desktop: 'xl' }}>
                            <Box flex="auto" minWidth="0">
                              <Box fontSize="sm" fontWeight="medium" margin="0 0 sm 0">
                                Fuel Type
                              </Box>
                              <Field
                                component={FormikRadioGroup}
                                id={`systems.${systemIndex}[${dataKey}].items.${index}.fuelType`}
                                name={`systems.${systemIndex}[${dataKey}].items.${index}.fuelType`}
                                options={fuelTypeOptions}
                                isDisabled
                                onChange={(event: SyntheticEvent<HTMLInputElement>) => {
                                  const selectedValue = (event.target as HTMLInputElement).value;
                                  if (typeof selectedValue !== 'undefined') {
                                    setFieldValue(
                                      `systems.${systemIndex}[${dataKey}].items.${index}.fuelType`,
                                      selectedValue,
                                    );
                                  }
                                }}
                              />
                            </Box>

                            <Box flex="auto" minWidth="0">
                              <Box fontSize="sm" fontWeight="medium" margin="0 0 sm 0">
                                Water Heater Type
                              </Box>
                              <Field
                                component={FormikRadioGroup}
                                id={`systems.${systemIndex}[${dataKey}].items.${index}.waterHeaterType`}
                                name={`systems.${systemIndex}[${dataKey}].items.${index}.waterHeaterType`}
                                options={waterHeaterTypeOptions}
                                isDisabled
                                onChange={(event: SyntheticEvent<HTMLInputElement>) => {
                                  const selectedValue = (event.target as HTMLInputElement).value;
                                  if (typeof selectedValue !== 'undefined') {
                                    setFieldValue(
                                      `systems.${systemIndex}[${dataKey}].items.${index}.waterHeaterType`,
                                      selectedValue,
                                    );
                                    setFieldValue(
                                      `systems.${systemIndex}[${dataKey}].items.${index}.tankCapacity`,
                                      undefined,
                                    );
                                    setFieldValue(
                                      `systems.${systemIndex}[${dataKey}].items.${index}.tanklessFlowRate`,
                                      undefined,
                                    );
                                  }
                                }}
                              />
                            </Box>
                          </Box>

                          {showTankCapacity && (
                            <Box maxWidth="3xl">
                              <Field
                                label="Tank Capacity"
                                name={`systems.${systemIndex}[${dataKey}].items.${index}.tankCapacitySelect`}
                                id={`systems.${systemIndex}[${dataKey}].items.${index}.tankCapacitySelect`}
                                placeholder="Select tank capacity..."
                                component={FormikSelectInput}
                                options={tankCapacityOptions}
                                isDisabled
                                onChange={(event: {
                                  target: { value: { value: string; label: string } | null };
                                }) => {
                                  const selected = event?.target?.value;
                                  setFieldValue(
                                    `systems.${systemIndex}[${dataKey}].items.${index}.tankCapacitySelect`,
                                    selected ?? null,
                                  );
                                  if (selected) {
                                    setFieldValue(`systems.${systemIndex}[${dataKey}].items.${index}.tankCapacity`, {
                                      value: selected.value,
                                      unit: 'gallons',
                                    });
                                  } else {
                                    setFieldValue(
                                      `systems.${systemIndex}[${dataKey}].items.${index}.tankCapacity`,
                                      undefined,
                                    );
                                  }
                                }}
                                value={selectedTankCapacity}
                              />
                            </Box>
                          )}

                          {showTanklessFlowRate && (
                            <Box maxWidth="3xl">
                              <Field
                                label="Tankless Flow Rate"
                                name={`systems.${systemIndex}[${dataKey}].items.${index}.tanklessFlowRateSelect`}
                                id={`systems.${systemIndex}[${dataKey}].items.${index}.tanklessFlowRateSelect`}
                                placeholder="Select flow rate..."
                                component={FormikSelectInput}
                                options={tanklessFlowRateOptions}
                                isDisabled
                                onChange={(event: {
                                  target: { value: { value: string; label: string } | null };
                                }) => {
                                  const selected = event?.target?.value;
                                  setFieldValue(
                                    `systems.${systemIndex}[${dataKey}].items.${index}.tanklessFlowRateSelect`,
                                    selected ?? null,
                                  );
                                  if (selected) {
                                    setFieldValue(
                                      `systems.${systemIndex}[${dataKey}].items.${index}.tanklessFlowRate`,
                                      {
                                        value: selected.value,
                                        unit: 'GPM',
                                      },
                                    );
                                  } else {
                                    setFieldValue(
                                      `systems.${systemIndex}[${dataKey}].items.${index}.tanklessFlowRate`,
                                      undefined,
                                    );
                                  }
                                }}
                                value={selectedFlowRate}
                              />
                            </Box>
                          )}
                        </Box>
                      </Box>
                    );
                  },
                )}
              </Box>
            );
          }}
        />
      </FormSection>
    </>
  );
};
